import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LogOut, Search, ShoppingCart, ArrowLeft, Trash2, CreditCard, Download, Package, CheckCircle2, Sun, Moon, Target, History, Loader2, Lock } from 'lucide-react';
import api from '../api';
import ResultsFilters from './ResultsFilters';
import { LEADS_PER_PACKAGE } from '../hooks/useLeadSelection';
import { calcPricing } from '../hooks/usePricing';

export default function ClientLeadsView({
  leadView, setLeadView,
  leadRenderableRows, leadSelectionMap,
  leadSelectedCount, leadSelectedPackages, leadSelectedMissingToNextPack,
  leadCartItems, setLeadCartItems,
  leadCartCount, leadCartPackages,
  leadCheckoutLoading, leadLastOrderId,
  pricingTier, setPricingTier,
  resultSearchInput, setResultSearchInput,
  categoryFilter, setCategoryFilter,
  sourceFilter, setSourceFilter,
  resultStateFilter, setResultStateFilter,
  contactFilter, setContactFilter,
  availableCategories, availableSources, availableResultStates,
  onAddToWallet, onClearSelection, onCheckout, onLogout,
  onLeadCheckboxClick, onDownloadPricingCSV,
  theme,
  toggleTheme,
}) {
  const isBase = leadView === 'base';
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (leadView === 'history') {
      setLoadingOrders(true);
      api.get('/client/orders')
        .then(res => setClientOrders(res.data))
        .catch(err => console.error('Erro ao buscar pedidos:', err))
        .finally(() => setLoadingOrders(false));
    }
  }, [leadView]);

  // Compute already purchased leads
  const purchasedLeadCodes = useMemo(() => {
    const codes = new Set();
    clientOrders.forEach(order => {
      // Consider both paid and delivered orders (and maybe created if we want to prevent double-booking)
      if (order.status === 'paid' || order.status === 'delivered' || order.status === 'approved') {
        const notes = order.notes || '';
        const match = notes.match(/codes=([^|]+)/);
        if (match && match[1]) {
          match[1].split(',').forEach(c => codes.add(c));
        }
      }
    });
    return codes;
  }, [clientOrders]);

  const baseParentRef = useRef(null);
  const baseRowVirtualizer = useVirtualizer({
    count: leadRenderableRows.length,
    getScrollElement: () => baseParentRef.current,
    estimateSize: () => 50,
    overscan: 5,
  });
  const baseVirtualItems = baseRowVirtualizer.getVirtualItems();
  const basePadTop = baseVirtualItems.length > 0 ? baseVirtualItems[0]?.start || 0 : 0;
  const basePadBottom = baseVirtualItems.length > 0 ? baseRowVirtualizer.getTotalSize() - (baseVirtualItems[baseVirtualItems.length - 1]?.end || 0) : 0;

  const cartParentRef = useRef(null);
  const cartRowVirtualizer = useVirtualizer({
    count: leadCartItems.length,
    getScrollElement: () => cartParentRef.current,
    estimateSize: () => 50,
    overscan: 5,
  });
  const cartVirtualItems = cartRowVirtualizer.getVirtualItems();
  const cartPadTop = cartVirtualItems.length > 0 ? cartVirtualItems[0]?.start || 0 : 0;
  const cartPadBottom = cartVirtualItems.length > 0 ? cartRowVirtualizer.getTotalSize() - (cartVirtualItems[cartVirtualItems.length - 1]?.end || 0) : 0;

  return (
    <div className="client-portal">
      {/* ── Top Nav Bar ───────────────────── */}
      <header className="client-topbar">
        <div className="client-topbar__brand">
          <div className="client-brand">
            <Target size={24} />
            <span>LeadsZone Maps</span>
          </div>
        </div>
        <div className="client-topbar__actions">
          <button
            className={`client-topbar__tab ${isBase ? 'active' : ''}`}
            onClick={() => setLeadView('base')}
          >
            <Search size={14} /> Base
          </button>
          <button
            className={`client-topbar__tab ${leadView === 'cart' ? 'active' : ''}`}
            onClick={() => setLeadView('cart')}
          >
            <ShoppingCart size={14} />
            Carteira
            {leadCartCount > 0 && <span className="client-topbar__badge">{leadCartCount}</span>}
          </button>
          <button
            className={`client-topbar__tab ${leadView === 'history' ? 'active' : ''}`}
            onClick={() => setLeadView('history')}
          >
            <History size={14} /> Histórico
          </button>
          <button className="client-topbar__tab" onClick={toggleTheme} title="Alternar tema" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />} Tema
          </button>
          <a
            href="https://www.whatszone.app.br"
            target="_blank"
            rel="noreferrer"
            className="client-topbar__tab"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'transparent', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}
          >
            🚀 WhatsZone
          </a>
          <button className="client-topbar__logout" onClick={onLogout}>
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>

      {/* ── Main Content ─────────────────── */}
      <main className="client-main">
        {leadView === 'base' && (
          /* ══════ BASE VIEW ══════ */
          <div className="client-base-view animate-fade-in">
            <div className="client-section-header">
              <div>
                <h1 className="client-section-title">
                  Base de <span className="accent-text">Leads</span>
                </h1>
                <p className="client-section-subtitle">Explore, filtre e adicione leads à sua carteira para compra.</p>
              </div>
            </div>

            <ResultsFilters
              resultSearchInput={resultSearchInput} setResultSearchInput={setResultSearchInput}
              categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
              sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
              resultStateFilter={resultStateFilter} setResultStateFilter={setResultStateFilter}
              contactFilter={contactFilter} setContactFilter={setContactFilter}
              availableCategories={availableCategories}
              availableSources={availableSources}
              availableResultStates={availableResultStates}
            />

            {/* Selection Action Bar */}
            <div className="client-selection-bar">
              <div className="client-selection-bar__info">
                <div className="client-stat-chip">
                  <CheckCircle2 size={14} />
                  <span>Selecionados: <strong>{leadSelectedCount}</strong></span>
                </div>
                <div className="client-stat-chip client-stat-chip--muted">
                  Dica: <strong>Shift + clique</strong> para selecionar faixa
                </div>
              </div>
              <div className="client-selection-bar__actions">
                <button className="client-btn client-btn--ghost" onClick={onClearSelection}>
                  <Trash2 size={13} /> Limpar seleção
                </button>
                <button className="client-btn client-btn--primary" onClick={onAddToWallet}>
                  <ShoppingCart size={14} /> Adicionar à Carteira
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="client-table-wrap" ref={baseParentRef} style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {leadRenderableRows.length === 0 ? (
                <div className="client-empty-state">
                  <Search size={56} />
                  <p>Nenhum lead encontrado com os filtros atuais.</p>
                </div>
              ) : (
                <table className="results-table client-results-table">
                  <thead>
                    <tr>
                      <th style={{ width: '46px' }}>Sel.</th>
                      <th style={{ width: '46px', textAlign: 'center' }}>#</th>
                      <th>ID Lead</th>
                      <th>Estado</th>
                      <th>Segmento</th>
                      <th>Whats (início)</th>
                      <th>E-mail (início)</th>
                      <th>Fonte</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {basePadTop > 0 && <tr><td style={{ height: `${basePadTop}px`, padding: 0, border: 0 }} colSpan={9} /></tr>}
                    {baseVirtualItems.map((virtualRow) => {
                      const itemIndex = virtualRow.index;
                      const item = leadRenderableRows[itemIndex];
                      const checked = Boolean(leadSelectionMap[item.key]);
                      const isPurchased = purchasedLeadCodes.has(item.code);
                      return (
                        <tr key={item.key} className={checked ? 'row-selected' : ''} style={isPurchased ? { opacity: 0.6, background: 'rgba(255,255,255,0.02)' } : {}}>
                          <td>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isPurchased}
                              onChange={() => {}}
                              onClick={(e) => {
                                if (!isPurchased) onLeadCheckboxClick(e, item, itemIndex);
                              }}
                              className="client-checkbox"
                            />
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.85em', textAlign: 'center', fontWeight: 600 }}>{itemIndex + 1}</td>
                          <td className="td-code">{item.code}</td>
                          <td>{item.estado}</td>
                          <td>{item.categoria}</td>
                          <td>{item.whatsappPreview}</td>
                          <td>{item.emailPreview}</td>
                          <td>{item.fonte}</td>
                          <td>
                            {isPurchased
                              ? <span className="status-badge" style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' }}>Comprado</span>
                              : checked
                                ? <span className="status-badge status-badge--selected">Selecionado</span>
                                : <span className="status-badge status-badge--available">Disponível</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {basePadBottom > 0 && <tr><td style={{ height: `${basePadBottom}px`, padding: 0, border: 0 }} colSpan={9} /></tr>}
                  </tbody>
                </table>
              )}
            </div>

            <div className="client-table-footer">
              Dados sensíveis (telefone, WhatsApp, e-mail, nome e endereço completo) permanecem ocultos no modo lead.
            </div>
          </div>
        )}
        
        {leadView === 'cart' && (
          /* ══════ CART / CHECKOUT VIEW ══════ */
          <div className="client-checkout-view animate-fade-in">
            <div className="client-section-header">
              <div>
                <h1 className="client-section-title">
                  <ShoppingCart size={22} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Carteira de <span className="accent-text">Leads</span>
                </h1>
                <p className="client-section-subtitle">Confirme os IDs para pagamento sem exposição dos dados dos leads.</p>
              </div>
              <div className="client-stat-chip client-stat-chip--lg">
                <Package size={16} /> Total: <strong>{leadCartCount}</strong> leads
              </div>
            </div>

            {/* Checkout Actions */}
            <div className="checkout-actions-bar">
              <button className="client-btn client-btn--ghost" onClick={() => setLeadView('base')}>
                <ArrowLeft size={14} /> Voltar para Base
              </button>
              <button className="client-btn client-btn--ghost" onClick={() => { setLeadCartItems([]); setLeadView('base'); }}>
                <Trash2 size={13} /> Limpar carteira
              </button>
              <button
                className="client-btn client-btn--checkout"
                onClick={onCheckout}
                disabled={leadCheckoutLoading || leadCartCount === 0}
              >
                <CreditCard size={15} />
                {leadCheckoutLoading ? 'Processando…' : 'Confirmar pagamento'}
              </button>
            </div>

            {/* TRUST BADGE STRIPE */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(99, 91, 255, 0.05)', border: '1px solid rgba(99, 91, 255, 0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <Lock size={24} color="#635BFF" style={{ minWidth: '24px' }} />
              <div>
                <strong style={{ color: 'var(--text-primary)', display: 'block', fontSize: '0.95rem', marginBottom: '4px' }}>Pagamento 100% Seguro via Stripe</strong>
                Seus dados financeiros são criptografados e processados diretamente pela <strong>Stripe</strong>, a maior e mais segura plataforma de pagamentos do mundo. Nós não temos acesso aos dados do seu cartão.
              </div>
            </div>

            {/* Pricing + Order Card */}
            <div className="checkout-grid">
              <div className="checkout-pricing-card">
                <h4 className="checkout-pricing-title">Precificação da carteira</h4>
                {(() => {
                  const packsForCalc = Math.max(1, Math.ceil(leadCartCount / LEADS_PER_PACKAGE));
                  let c = calcPricing(pricingTier, packsForCalc);
                  if (leadCartCount === 0) {
                    c = { subtotal: 0, discount: c.discount, discountAmount: 0, total: 0, perLead: 0 };
                  }
                  return (
                    <div className="checkout-pricing-body">
                      <div className="checkout-pricing-row">
                        <span>Plano</span>
                        <select value={pricingTier} onChange={(e) => setPricingTier(e.target.value)} className="checkout-plan-select">
                          <option value="basic">Básico</option>
                          <option value="standard">Padrão</option>
                          <option value="premium">Premium</option>
                        </select>
                      </div>
                      <div className="checkout-divider" />
                      <div className="checkout-pricing-row">
                        <span>Leads na carteira</span>
                        <strong>{leadCartCount}</strong>
                      </div>
                      <div className="checkout-pricing-row">
                        <span>Subtotal</span>
                        <strong>R$ {c.subtotal.toFixed(2)}</strong>
                      </div>
                      <div className="checkout-pricing-row checkout-pricing-row--discount">
                        <span>Desconto ({(c.discount * 100).toFixed(0)}%)</span>
                        <strong>- R$ {c.discountAmount.toFixed(2)}</strong>
                      </div>
                      <div className="checkout-divider" />
                      <div className="checkout-pricing-row checkout-pricing-row--total">
                        <span>Total</span>
                        <strong>R$ {c.total.toFixed(2)}</strong>
                      </div>
                      <div className="checkout-pricing-row checkout-pricing-row--per-lead">
                        <span>Preço por lead</span>
                        <strong>R$ {c.perLead.toFixed(2)}</strong>
                      </div>
                      <button className="client-btn client-btn--ghost checkout-export-btn" onClick={onDownloadPricingCSV}>
                        <Download size={13} /> Exportar tabela de precificação
                      </button>
                    </div>
                  );
                })()}
              </div>

              <div className="checkout-pricing-card" style={{ background: 'var(--glass-bg)' }}>
                <h4 className="checkout-pricing-title">Resumo dos Dados</h4>
                {(() => {
                  const whatsCount = leadCartItems.filter(i => i.whatsappPreview !== '---').length;
                  const emailCount = leadCartItems.filter(i => i.emailPreview !== '---').length;
                  const whatsPercent = leadCartCount > 0 ? Math.round((whatsCount / leadCartCount) * 100) : 0;
                  const emailPercent = leadCartCount > 0 ? Math.round((emailCount / leadCartCount) * 100) : 0;

                  return (
                    <div className="checkout-pricing-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                        Visão geral do preenchimento de contato dos <strong>{leadCartCount}</strong> leads selecionados:
                      </p>
                      <div className="checkout-divider" />
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: whatsPercent > 0 ? '#25D366' : 'var(--text-muted)' }}></div>
                          <span>WhatsApp / Telefone</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <strong>{whatsCount}</strong> <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>({whatsPercent}%)</span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: emailPercent > 0 ? '#EA4335' : 'var(--text-muted)' }}></div>
                          <span>E-mail</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <strong>{emailCount}</strong> <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>({emailPercent}%)</span>
                        </div>
                      </div>

                      <div className="checkout-divider" style={{ marginTop: 'auto' }} />
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                        <CheckCircle2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                        Apenas os leads que você aprovar na tabela abaixo serão processados no seu checkout.
                      </p>
                    </div>
                  );
                })()}
              </div>

              {leadLastOrderId && (
                <div className="checkout-order-card" style={{ gridColumn: '1 / -1' }}>
                  <CheckCircle2 size={22} />
                  <div>
                    <span>Pedido gerado</span>
                    <strong>{leadLastOrderId}</strong>
                  </div>
                </div>
              )}
            </div>

            {/* Cart Table */}
            <div className="client-table-wrap" ref={cartParentRef} style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {leadCartCount === 0 ? (
                <div className="client-empty-state">
                  <ShoppingCart size={56} />
                  <p>Nenhum lead adicionado na carteira.</p>
                </div>
              ) : (
                <table className="results-table client-results-table">
                  <thead>
                    <tr>
                      <th>ID Lead</th>
                      <th>Estado</th>
                      <th>Segmento</th>
                      <th>Whats (início)</th>
                      <th>E-mail (início)</th>
                      <th>Fonte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartPadTop > 0 && <tr><td style={{ height: `${cartPadTop}px`, padding: 0, border: 0 }} colSpan={6} /></tr>}
                    {cartVirtualItems.map((virtualRow) => {
                      const item = leadCartItems[virtualRow.index];
                      return (
                      <tr key={item.key}>
                        <td className="td-code">{item.code}</td>
                        <td><span className="ui-badge ui-badge--neutral">{item.estado || 'N/D'}</span></td>
                        <td><span className="ui-badge ui-badge--cyan">{item.categoria || 'N/D'}</span></td>
                        <td style={{ color: item.whatsappPreview !== '---' ? '#25D366' : 'var(--text-muted)' }}>{item.whatsappPreview}</td>
                        <td style={{ color: item.emailPreview !== '---' ? '#EA4335' : 'var(--text-muted)' }}>{item.emailPreview}</td>
                        <td><span className="ui-badge ui-badge--purple">{item.fonte || 'Google API'}</span></td>
                      </tr>
                      );
                    })}
                    {cartPadBottom > 0 && <tr><td style={{ height: `${cartPadBottom}px`, padding: 0, border: 0 }} colSpan={6} /></tr>}
                  </tbody>
                </table>
              )}
            </div>

            <div className="client-table-footer">
              Verifique os leads adicionados antes de confirmar a compra.
            </div>
          </div>
        )}

        {leadView === 'history' && (
          <div className="client-history-view animate-fade-in" style={{ padding: '0' }}>
            <div className="client-section-header">
              <div>
                <h1 className="client-section-title">
                  <History size={22} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Histórico de <span className="accent-text">Compras</span>
                </h1>
                <p className="client-section-subtitle">Acompanhe seus pedidos, pagamentos e acesse as planilhas de leads baixadas.</p>
              </div>
            </div>

            <div className="client-table-wrap" style={{ maxHeight: '600px', overflowY: 'auto', marginTop: '1.5rem' }}>
              {loadingOrders ? (
                <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
                  <Loader2 className="spin" size={32} color="var(--accent)" />
                </div>
              ) : clientOrders.length === 0 ? (
                <div className="client-empty-state">
                  <Package size={56} />
                  <p>Você ainda não realizou nenhuma compra.</p>
                </div>
              ) : (
                <table className="results-table client-results-table">
                  <thead>
                    <tr>
                      <th>ID do Pedido</th>
                      <th>Data</th>
                      <th>Leads</th>
                      <th>Valor (Plano)</th>
                      <th>Status</th>
                      <th>Exportação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientOrders.map(order => {
                      const date = new Date(order.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                      const totalLeads = order.packs * LEADS_PER_PACKAGE;
                      const statusMap = {
                        created: { label: 'Aguardando Pagamento', color: 'neutral' },
                        paid: { label: 'Pago', color: 'green' },
                        requested_export: { label: 'Exportando...', color: 'cyan' },
                        completed: { label: 'Concluído', color: 'green' }
                      };
                      const s = statusMap[order.status] || { label: order.status, color: 'neutral' };

                      return (
                        <tr key={order.id}>
                          <td className="td-code" style={{ fontFamily: 'monospace' }}>{order.id.slice(0, 8).toUpperCase()}</td>
                          <td>{date}</td>
                          <td><strong>{totalLeads}</strong> leads</td>
                          <td style={{ textTransform: 'capitalize' }}>{order.tier}</td>
                          <td><span className={`ui-badge ui-badge--${s.color}`}>{s.label}</span></td>
                          <td>
                            {order.export_url ? (
                              <a href={order.export_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--tl-cyan)', textDecoration: 'none', fontWeight: 600 }}>
                                <Download size={14} /> Baixar Planilha
                              </a>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>{order.status === 'paid' ? 'Aguarde processamento...' : '-'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </main>

      <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid var(--glass-panel-border)', fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 'auto', background: 'var(--bg-main)' }}>
        🚀 Quer disparar mensagens em massa no WhatsApp para esses leads? <a href="https://www.whatszone.app.br" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Conheça a ferramenta WhatsZone</a>
      </div>
    </div>
  );
}
